import { useState, useEffect, useCallback, useRef, ReactNode } from 'react';

import { Loader2 } from 'lucide-react';

import { IPaginatedListResponse } from '@/api/types';
import { IcClickout } from '@/assets/images';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/TailwindComponents';
import { useToastStore } from '@/store';

interface AsyncSelectProps<T> {
  fetchOptions: (
    query: string,
    page: number,
  ) => Promise<IPaginatedListResponse<T>>;
  onSelect: (option: T | null) => void;
  value?: T | null;
  getOptionLabel?: (option: T) => string;
  getOptionValue?: (option: T) => string;
  renderOption?: (option: T) => React.ReactNode;
  debounceMs?: number;
  pageSize?: number;
  label?: string;
  disabled?: boolean;
  className?: string;
  itemAction?: (option: T) => ReactNode;
  onItemAction?: (
    option: T,
    event: React.MouseEvent,
    disabled: boolean,
  ) => void;
}

export function AsyncSelect<T>({
  fetchOptions,
  onSelect,
  value = null,
  getOptionLabel = (option: any) => option.label || option.toString(),
  getOptionValue = (option: any) => option.id || option.toString(),
  renderOption,
  debounceMs = 500,
  label = 'Select...',
  disabled = false,
  className = '',
  itemAction,
  onItemAction,
}: AsyncSelectProps<T>) {
  const [input, setInput] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');
  const [options, setOptions] = useState<T[]>([]);
  const [selected, setSelected] = useState<T | null>(value);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout>();
  const inputRef = useRef<HTMLInputElement>(null);
  const { updateToast } = useToastStore();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setDebouncedInput(input);
    }, debounceMs);

    return () => clearTimeout(debounceRef.current);
  }, [input, debounceMs]);

  const loadOptions = useCallback(
    async (q: string, p: number, reset = false) => {
      setLoading(true);
      try {
        const res = await fetchOptions(q, p);
        setOptions((prev) => {
          if (reset) {
            return res.docs;
          }

          // Deduplicate by unique identifier
          const existingIds = new Set(prev.map((item) => getOptionValue(item)));
          const newItems = res.docs.filter(
            (item) => !existingIds.has(getOptionValue(item)),
          );

          return [...prev, ...newItems];
        });
        setHasNextPage(res.hasNextPage);
        setPage(res.page);
        setTimeout(() => inputRef.current?.focus(), 0);
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
      } finally {
        setLoading(false);
      }
    },
    [fetchOptions, getOptionValue],
  );

  useEffect(() => {
    loadOptions(debouncedInput, 1, true);
  }, [debouncedInput, loadOptions]);

  useEffect(() => {
    if (!value || selected === null) {
      setSelected(null);
      return;
    }

    const matchedOption = options.find(
      (opt) => (opt as { id: string }).id === value,
    );

    if (matchedOption) {
      setSelected(matchedOption);
    }
  }, [value, options, getOptionValue]);

  const handleChange = (val: string) => {
    const option = options.find((opt) => getOptionValue(opt) === val) || null;
    setSelected(option);
    onSelect(option);
  };

  const handleClear = () => {
    setSelected(null);
    onSelect(null);
  };

  const handleItemAction = (option: T, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (onItemAction) {
      onItemAction(option, event, disabled);
    }
  };

  return (
    <div className={className}>
      <div className="font-medium text-sm pb-0.5">{label}</div>
      <div className="flex gap-2 w-full">
        <div className="relative w-full">
          <Select
            key={selected ? getOptionValue(selected) : 'empty'}
            value={selected ? getOptionValue(selected) : undefined}
            onValueChange={handleChange}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue>
                {selected ? <span>{getOptionLabel(selected)}</span> : null}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <div className="px-2 pb-1">
                <Input
                  ref={inputRef}
                  className="w-full rounded border-input px-2 py-1 text-sm"
                  placeholder="Search..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </div>
              ) : options.length === 0 ? (
                <div className="px-2 py-2 text-sm text-muted-foreground">
                  No results found
                </div>
              ) : (
                [
                  ...(selected &&
                  !options.find(
                    (o) => getOptionValue(o) === getOptionValue(selected),
                  )
                    ? [selected]
                    : []),
                  ...options,
                ].map((option) => {
                  const l = getOptionLabel(option);
                  const v = getOptionValue(option);
                  return (
                    <SelectItem className="!w-full" key={v} value={v}>
                      <div className="flex gap-12 items-center justify-between !w-full">
                        <span className="truncate w-full">
                          {renderOption ? renderOption(option) : l}
                        </span>
                        {itemAction && (
                          <div
                            onPointerDown={(e) => handleItemAction(option, e)}
                          >
                            {itemAction(option)}
                          </div>
                        )}
                      </div>
                    </SelectItem>
                  );
                })
              )}

              {hasNextPage && !loading && (
                <div className="flex justify-center p-2">
                  <button
                    className="text-xs text-blue-600 hover:underline"
                    onClick={() => loadOptions(debouncedInput, page + 1)}
                  >
                    Load more
                  </button>
                </div>
              )}
            </SelectContent>
          </Select>
          {selected && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-6 top-0.5 px-2 py-1 text-[14px] hover:text-gray-700"
            >
              ✕
            </button>
          )}
        </div>
        {selected && itemAction?.(selected) ? (
          <div
            onPointerDown={(e) => {
              if (onItemAction && selected) {
                handleItemAction(selected, e);
              }
            }}
          >
            <Button type="button" variant="outline" className="h-9 w-9">
              <IcClickout className="w-full h-full" />
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
